import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import PrescriptionService, {
  CreatePrescriptionData,
  Medicine,
  PrescriptionItem,
  DrugDetails,
  Prescription,
} from "../../../../services/prescriptionService";
import SearchableInput from "./SearchableInput";

interface PrescriptionModalProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  onPrescriptionCreated?: (prescriptionId: number) => void;
  onPrescriptionUpdated?: (prescriptionId: number) => void;
  appointmentId?: number;
  editMode?: boolean;
  prescriptionToEdit?: Prescription;
}

const PrescriptionModal: React.FC<PrescriptionModalProps> = ({
  patient,
  isOpen,
  onClose,
  onPrescriptionCreated,
  onPrescriptionUpdated,
  appointmentId,
  editMode = false,
  prescriptionToEdit,
}) => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Medicine[]>([]);
  const [selectedMedicines, setSelectedMedicines] = useState<
    PrescriptionItem[]
  >([]);
  const [multipleSimilarMedicines, setMultipleSimilarMedicines] = useState<{
    originalSearch: string;
    found: Array<{ name: string; similar: Medicine[] }>;
    notFound: Array<{ name: string }>;
  } | null>(null);

  const [bulkAddMedicines, setBulkAddMedicines] = useState<
    Array<{
      name: string;
      strength: string;
      form: string;
      category: string;
    }>
  >([]);

  const [showBulkAddForm, setShowBulkAddForm] = useState(false);
  const [diagnosis, setDiagnosis] = useState("");
  const [doctorNotes, setDoctorNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDrugDetails, setShowDrugDetails] = useState<{
    [key: number]: boolean;
  }>({});
  const [drugInteractions, setDrugInteractions] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [manualEntry, setManualEntry] = useState("");
  const [manualEntryError, setManualEntryError] = useState("");

  const [showQuickAddForm, setShowQuickAddForm] = useState(false);
  const [quickAddData, setQuickAddData] = useState<{
    name: string;
    strength: string;
    form: string;
    category: string;
  }>({
    name: "",
    strength: "",
    form: "tablet",
    category: "Supplement",
  });
  const [isQuickAdding, setIsQuickAdding] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [prescriptionId, setPrescriptionId] = useState<number | null>(null);
  // Duration options
  const durationOptions = [
    { value: "3_days", label: "3 days | ٣ أيام" },
    { value: "5_days", label: "5 days | ٥ أيام" },
    { value: "7_days", label: "1 week | أسبوع" },
    { value: "10_days", label: "10 days | ١٠ أيام" },
    { value: "14_days", label: "2 weeks | أسبوعان" },
    { value: "21_days", label: "3 weeks | ٣ أسابيع" },
    { value: "30_days", label: "1 month | شهر واحد" },
    { value: "60_days", label: "2 months | شهران" },
    { value: "90_days", label: "3 months | ٣ أشهر" },
    { value: "ongoing", label: "Ongoing | مستمر" },
    { value: "as_needed", label: "As needed | حسب الحاجة" },
  ];

  // Meal timing options
  const mealTimingOptions = [
    { value: "anytime", label: "Anytime" },
    { value: "before", label: "Before meals" },
    { value: "after", label: "After meals" },
    { value: "with", label: "With meals" },
  ];
  const medicineFormOptions = [
    { value: "tablet", label: "Tablet" },
    { value: "capsule", label: "Capsule" },
    { value: "syrup", label: "Syrup" },
    { value: "injection", label: "Injection" },
    { value: "cream", label: "Cream" },
    { value: "drops", label: "Drops" },
    { value: "inhaler", label: "Inhaler" },
    { value: "suppository", label: "Suppository" },
    { value: "patch", label: "Patch" },
    { value: "gel", label: "Gel" },
    { value: "powder", label: "Powder" },
    { value: "solution", label: "Solution" },
    { value: "other", label: "Other" },
  ];
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  // Search medicines with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setSearchLoading(true);
        try {
          const results = await PrescriptionService.searchMedicines(
            searchQuery
          );
          setSearchResults(results);
        } catch (error) {
          console.error("Medicine search failed:", error);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Check for drug interactions when medicines change
  useEffect(() => {
    const checkInteractions = async () => {
      if (selectedMedicines.length > 1) {
        try {
          // Filter out custom medicines (no medicine_id) before checking interactions
          const medicineIds = selectedMedicines
            .map((item) => item.medicine_id)
            .filter((id): id is number => id !== null && id !== undefined);

          // Only check if we have at least 2 database medicines
          if (medicineIds.length > 1) {
            const interactions =
              await PrescriptionService.checkDrugInteractions(medicineIds);
            setDrugInteractions(interactions.map((i) => i.description));
          } else {
            setDrugInteractions([]);
          }
        } catch (error) {
          console.error("Failed to check drug interactions:", error);
          setDrugInteractions([]);
        }
      } else {
        setDrugInteractions([]);
      }
    };

    checkInteractions();
  }, [selectedMedicines]);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedMedicines([]);
      setDiagnosis("");
      setDoctorNotes("");
      setErrors({});
      setShowDrugDetails({});
      setDrugInteractions([]);
      setIsEditMode(false);
      setPrescriptionId(null); // NEW
    }
  }, [isOpen]);
  const categoryOptions = [
    "Supplement",
    "Vitamin",
    "Antibiotic",
    "Analgesic",
    "Antipyretic",
    "Antihypertensive",
    "Antidiabetic",
    "Other",
  ];
  // Load prescription data when editing
  useEffect(() => {
    if (isOpen && editMode && prescriptionToEdit) {
      console.log("Loading prescription for edit:", prescriptionToEdit);

      setIsEditMode(true);
      setPrescriptionId(prescriptionToEdit.id || null);
      setDiagnosis(prescriptionToEdit.diagnosis || "");
      setDoctorNotes(prescriptionToEdit.doctor_notes || "");

      // Convert prescription items to selectedMedicines format
      const medicines: PrescriptionItem[] = prescriptionToEdit.items.map(
        (item) => ({
          id: item.id,
          medicine_id: item.medicine_id,
          medicine: item.medicine,
          dosage: item.dosage,
          frequency: item.frequency,
          duration: item.duration,
          instructions: item.instructions || "",
          doctor_notes: item.doctor_notes || "",
          quantity: item.quantity || 1,
          before_after_meal: item.before_after_meal,
          prescription_hint_id: item.prescription_hint_id,
          prescriptionHint: item.prescriptionHint,
        })
      );

      setSelectedMedicines(medicines);
    }
  }, [isOpen, editMode, prescriptionToEdit]);
  // Handler for quick add
  const handleQuickAddMedicine = async () => {
    if (!quickAddData.name.trim()) {
      setErrors({ quickAdd: "Medicine name is required" });
      return;
    }

    setIsQuickAdding(true);
    try {
      const newMedicine = await PrescriptionService.quickAddMedicine({
        name: quickAddData.name.trim(),
        strength: quickAddData.strength.trim() || undefined,
        form: quickAddData.form as any,
        category: quickAddData.category || undefined,
      });

      // Add the newly created medicine to the prescription
      addMedicine(newMedicine);

      // Reset form and close
      setQuickAddData({
        name: "",
        strength: "",
        form: "tablet",
        category: "Supplement",
      });
      setShowQuickAddForm(false);
      setErrors({});

      // Show success message
      setSuccessMessage(
        `Medicine "${newMedicine.name}" added to database successfully!`
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      setErrors({ quickAdd: error.message || "Failed to add medicine" });
    } finally {
      setIsQuickAdding(false);
    }
  };

  const addMedicine = (medicine: Medicine) => {
    // Check if medicine already added
    if (selectedMedicines.some((item) => item.medicine_id === medicine.id)) {
      setErrors({
        medicine: "This medicine is already added to the prescription",
      });
      return;
    }

    const newItem: PrescriptionItem = {
      medicine_id: medicine.id,
      medicine: medicine,
      dosage: "",
      frequency: "",
      duration: "",
      instructions: "",
      doctor_notes: "",
      quantity: 1,
      before_after_meal: undefined,
    };

    setSelectedMedicines((prev) => [...prev, newItem]);
    setSearchQuery("");
    setSearchResults([]);
    setErrors({});
  };

  const updateMedicineItem = (
    index: number,
    field: keyof PrescriptionItem,
    value: any
  ) => {
    setSelectedMedicines((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeMedicine = (index: number) => {
    setSelectedMedicines((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleDrugDetails = (index: number) => {
    setShowDrugDetails((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const validatePrescription = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (selectedMedicines.length === 0) {
      newErrors.medicines = "Please add at least one medicine";
    }

    // Dosage, frequency, duration, timing, and diagnosis are now optional
    // No validation required for these fields

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update the medicine display to show custom medicines
  const getMedicineDisplayName = (item: PrescriptionItem): string => {
    if (item.medicine) {
      return PrescriptionService.getMedicineDisplayName(item.medicine);
    }
    return "Unknown Medicine";
  };

  const handleSave = async () => {
    if (!validatePrescription()) return;

    setIsSaving(true);
    try {
      const prescriptionData: any = {
        patient_id: patient.id,
        appointment_id: appointmentId,
        prescription_date: new Date().toISOString().split("T")[0],
        diagnosis,
        doctor_notes: doctorNotes,
        items: selectedMedicines.map((item) => ({
          ...(item.id && { id: item.id }), // Include ID for updates
          medicine_id: item.medicine_id,
          dosage: item.dosage,
          frequency: item.frequency,
          duration: item.duration,
          instructions: item.instructions || "",
          doctor_notes: item.doctor_notes || "",
          quantity: item.quantity || 1,
          before_after_meal: item.before_after_meal,
        })),
      };

      let prescription;
      if (isEditMode && prescriptionId) {
        // UPDATE EXISTING PRESCRIPTION
        prescription = await PrescriptionService.updatePrescription(
          prescriptionId,
          prescriptionData
        );

        if (onPrescriptionUpdated) {
          onPrescriptionUpdated(prescription.id!);
        }
      } else {
        // CREATE NEW PRESCRIPTION
        prescription = await PrescriptionService.createPrescription(
          prescriptionData
        );

        if (onPrescriptionCreated) {
          onPrescriptionCreated(prescription.id!);
        }
      }

      onClose();
    } catch (error: any) {
      setErrors({
        submit:
          error.message ||
          `Failed to ${isEditMode ? "update" : "save"} prescription`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderDrugDetails = (medicine: Medicine, index: number) => {
    if (!medicine.drug_details || !showDrugDetails[index]) return null;

    const details = medicine.drug_details;

    return (
      <div
        style={{
          marginTop: "12px",
          padding: "12px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "6px",
          fontSize: "12px",
        }}
      >
        <h5
          style={{
            fontSize: "13px",
            fontWeight: "600",
            marginBottom: "8px",
            color: "#374151",
          }}
        >
          Drug Information
        </h5>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "8px",
          }}
        >
          {details.drug_number && (
            <div>
              <strong>Drug Number:</strong> {details.drug_number}
            </div>
          )}
          {details.laboratory && (
            <div>
              <strong>Laboratory:</strong> {details.laboratory}
            </div>
          )}
          {details.country_of_origin && (
            <div>
              <strong>Origin:</strong> {details.country_of_origin}
            </div>
          )}
          {details.drug_type && (
            <div>
              <strong>Type:</strong> {details.drug_type}
            </div>
          )}
        </div>

        {details.drug_brands && details.drug_brands.length > 0 && (
          <div style={{ marginTop: "8px", wordBreak: "break-word" }}>
            <strong>Available Brands:</strong> {details.drug_brands.join(", ")}
          </div>
        )}

        {details.alternatives && details.alternatives.length > 0 && (
          <div style={{ marginTop: "8px", wordBreak: "break-word" }}>
            <strong>Alternatives:</strong> {details.alternatives.join(", ")}
          </div>
        )}

        {(details.warnings || details.drug_interactions) && (
          <div
            style={{
              marginTop: "8px",
              padding: "8px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "4px",
              color: "#dc2626",
            }}
          >
            <strong>Safety Information:</strong>
            {details.warnings && <div>• {details.warnings}</div>}
            {details.drug_interactions && (
              <div>• Drug Interactions: {details.drug_interactions}</div>
            )}
          </div>
        )}

        {details.common_side_effects && (
          <div
            style={{
              marginTop: "8px",
              color: "#6b7280",
              wordBreak: "break-word",
            }}
          >
            <strong>Common Side Effects:</strong> {details.common_side_effects}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "center",
    padding: isMobile ? "10px" : "20px",
    overflow: "auto",
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "12px" : "16px",
    width: "100%",
    maxWidth: isMobile ? "100%" : "900px",
    maxHeight: isMobile ? "100%" : "90vh",
    overflow: "hidden",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
    margin: isMobile ? "10px 0" : "auto",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : isTablet ? "18px" : "20px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: isMobile ? "12px" : isTablet ? "14px" : "16px",
    flexShrink: 0,
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    padding: isMobile ? "16px" : "24px",
  };

  const footerStyle: React.CSSProperties = {
    padding: "20px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "16px" : "0",
    flexShrink: 0,
  };

  // 3. UPDATE buttonStyle FUNCTION (replace existing buttonStyle)
  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#3b82f6", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      danger: { background: "#ef4444", color: "white" },
    };

    return {
      padding: isMobile ? "8px 16px" : "10px 20px",
      borderRadius: "8px",
      border: "none",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      textAlign: "center",
      whiteSpace: "nowrap",
      ...variants[variant],
    };
  };

  // 4. UPDATE RESPONSIVE GRID STYLES (replace existing grid styles)
  const patientInfoGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : isTablet
      ? "repeat(2, 1fr)"
      : "repeat(4, 1fr)",
    gap: "12px",
  };

  const medicineGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "12px",
    marginTop: "12px",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease",
  };

  const patientInfoStyle: React.CSSProperties = {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "24px",
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle} className="nn">
          <div style={{ flex: 1 }}>
            <h2
              style={{
                fontSize: isMobile ? "18px" : isTablet ? "19px" : "20px",
                fontWeight: "600",
                margin: "0 0 4px 0",
                color: "#1f2937",
              }}
            >
              {isEditMode ? "✏️ Edit Prescription" : "💊 New Prescription"}
            </h2>
            <p
              style={{
                fontSize: isMobile ? "13px" : isTablet ? "13px" : "14px",
                color: "#64748b",
                margin: 0,
              }}
            >
              {isEditMode ? "Edit prescription for" : "Create prescription for"}{" "}
              {patient.full_name ||
                `${patient.first_name} ${patient.last_name}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ ...buttonStyle("secondary"), padding: "6px 10px" }}
          >
            ✕
          </button>
        </div>
        {/* Success Message */}
        {successMessage && (
          <div
            style={{
              padding: "12px 16px",
              background: "#dcfce7",
              border: "1px solid #86efac",
              borderRadius: "8px",
              color: "#166534",
              fontSize: "14px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>✅</span>
            <span>{successMessage}</span>
          </div>
        )}
        {/* Content */}
        <div style={contentStyle}>
          {/* Patient Info */}
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <div style={patientInfoGridStyle}>
              <div>
                <strong>Patient:</strong>{" "}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </div>
              <div>
                <strong>Age:</strong> {patient.age} years
              </div>
              <div>
                <strong>Gender:</strong> {patient.gender}
              </div>
              <div>
                <strong>Phone:</strong> {patient.phone}
              </div>
            </div>
            {patient.allergies && patient.allergies.length > 0 && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "8px 12px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  color: "#dc2626",
                }}
              >
                <strong>Allergies:</strong> {patient.allergies.join(", ")}
              </div>
            )}
            {patient.chronic_conditions &&
              patient.chronic_conditions.length > 0 && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    background: "#fef3c7",
                    border: "1px solid #f59e0b",
                    borderRadius: "6px",
                    color: "#92400e",
                  }}
                >
                  <strong>Chronic Conditions:</strong>{" "}
                  {patient.chronic_conditions.join(", ")}
                </div>
              )}
          </div>

          {/* Drug Interactions Warning */}
          {drugInteractions.length > 0 && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "24px",
              }}
            >
              <h4
                style={{
                  color: "#dc2626",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                Drug Interaction Warning
              </h4>
              {drugInteractions.map((interaction, index) => (
                <div
                  key={index}
                  style={{
                    color: "#dc2626",
                    fontSize: "13px",
                    marginBottom: "4px",
                  }}
                >
                  • {interaction}
                </div>
              ))}
            </div>
          )}

          {/* Diagnosis */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Diagnosis *
            </label>
            <input
              type="text"
              id="diagnosis"
              name="diagnosis"
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="Enter diagnosis"
              style={{
                ...inputStyle,
                borderColor: errors.diagnosis ? "#ef4444" : "#e5e7eb",
              }}
            />

            {errors.diagnosis && (
              <div
                style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
              >
                {errors.diagnosis}
              </div>
            )}
          </div>

          {/* Medicine Search */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Search & Add Medicine
            </label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                id="search_medicines"
                name="search_medicines"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search medicines..."
                style={inputStyle}
              />

              {searchLoading && (
                <div
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  Loading...
                </div>
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div
                style={{
                  marginTop: "8px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  maxHeight: "200px",
                  overflow: "auto",
                }}
              >
                {searchResults.map((medicine) => (
                  <div
                    key={medicine.id}
                    onClick={() => addMedicine(medicine)}
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid #f3f4f6",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8fafc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <div
                      style={{
                        fontWeight: "500",
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      {PrescriptionService.getMedicineDisplayName(medicine)}
                      {PrescriptionService.hasSafetyWarnings(medicine) && (
                        <span style={{ color: "#dc2626", fontSize: "12px" }}>
                          ⚠️
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>
                      {PrescriptionService.formatMedicineDetails(medicine)}
                    </div>
                    {medicine.drug_details && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6b7280",
                          marginTop: "2px",
                        }}
                      >
                        {PrescriptionService.formatDrugDetails(
                          medicine.drug_details
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {errors.medicine && (
              <div
                style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
              >
                {errors.medicine}
              </div>
            )}
            {errors.medicines && (
              <div
                style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
              >
                {errors.medicines}
              </div>
            )}
          </div>

          {/* Manual Entry Section - Add after the medicine search section */}
          {/* Manual Entry Section */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Or Manually Add Medicine to Database
              <span
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  fontWeight: "normal",
                  marginLeft: "8px",
                }}
              >
                (Enter one medicine per line or separate with commas)
              </span>
            </label>
            <textarea
              value={manualEntry}
              onChange={(e) => {
                setManualEntry(e.target.value);
                setManualEntryError("");
              }}
              placeholder="Example:&#10;Vitamin D 1000IU&#10;Omega 3, Magnesium&#10;"
              rows={4}
              style={{
                ...inputStyle,
                resize: "vertical",
                fontFamily: "monospace",
                fontSize: "13px",
                borderColor: manualEntryError ? "#ef4444" : "#e5e7eb",
              }}
            />
            {manualEntryError && (
              <div
                style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
              >
                {manualEntryError}
              </div>
            )}
            <button
              type="button"
              onClick={async () => {
                if (!manualEntry.trim()) {
                  setManualEntryError(
                    "Please enter at least one medicine name"
                  );
                  return;
                }

                const entries = manualEntry
                  .split(/[\n,]+/)
                  .map((entry) => entry.trim())
                  .filter((entry) => entry.length > 0);

                if (entries.length === 0) {
                  setManualEntryError("No valid medicine names found");
                  return;
                }

                // Process each entry
                const processResults: {
                  name: string;
                  exists: boolean;
                  similar: Medicine[];
                }[] = [];

                for (const medicineName of entries) {
                  // Skip if already in prescription
                  if (
                    selectedMedicines.some(
                      (item) =>
                        item.medicine?.name.toLowerCase() ===
                        medicineName.toLowerCase()
                    )
                  ) {
                    continue;
                  }

                  // Check for similar medicines
                  const similarMedicines =
                    await PrescriptionService.checkSimilarMedicines(
                      medicineName
                    );

                  processResults.push({
                    name: medicineName,
                    exists: similarMedicines.length > 0,
                    similar: similarMedicines,
                  });
                }

                const foundInDatabase = processResults.filter((r) => r.exists);
                const notFoundInDatabase = processResults.filter(
                  (r) => !r.exists
                );

                if (
                  foundInDatabase.length > 0 ||
                  notFoundInDatabase.length > 0
                ) {
                  setMultipleSimilarMedicines({
                    originalSearch: entries[0],
                    found: foundInDatabase,
                    notFound: notFoundInDatabase,
                  });
                  return;
                }

                setManualEntry("");
              }}
              style={{
                ...buttonStyle("secondary"),
                marginTop: "8px",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Check & Add Medicine(s)
            </button>
          </div>
          {/* Quick Add to Database Form */}
          {showQuickAddForm && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  padding: "24px",
                  maxWidth: "500px",
                  width: "100%",
                  maxHeight: "90vh",
                  overflow: "auto",
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                }}
              >
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    marginBottom: "16px",
                    color: "#1f2937",
                  }}
                >
                  Add Medicine to Database
                </h3>

                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Medicine Name *
                  </label>
                  <input
                    type="text"
                    value={quickAddData.name}
                    onChange={(e) =>
                      setQuickAddData({ ...quickAddData, name: e.target.value })
                    }
                    placeholder="e.g., Vitamin D3"
                    style={inputStyle}
                    autoFocus
                  />
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Strength (Optional)
                  </label>
                  <input
                    type="text"
                    value={quickAddData.strength}
                    onChange={(e) =>
                      setQuickAddData({
                        ...quickAddData,
                        strength: e.target.value,
                      })
                    }
                    placeholder="e.g., 1000 IU, 500mg"
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Form *
                  </label>
                  <select
                    value={quickAddData.form}
                    onChange={(e) =>
                      setQuickAddData({ ...quickAddData, form: e.target.value })
                    }
                    style={inputStyle}
                  >
                    {medicineFormOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Category
                  </label>
                  <select
                    value={quickAddData.category}
                    onChange={(e) =>
                      setQuickAddData({
                        ...quickAddData,
                        category: e.target.value,
                      })
                    }
                    style={inputStyle}
                  >
                    {categoryOptions.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {errors.quickAdd && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginBottom: "16px",
                      padding: "8px",
                      background: "#fef2f2",
                      borderRadius: "6px",
                    }}
                  >
                    {errors.quickAdd}
                  </div>
                )}

                <div
                  style={{
                    padding: "12px",
                    background: "#eff6ff",
                    border: "1px solid #dbeafe",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#1e40af",
                    marginBottom: "16px",
                  }}
                >
                  This medicine will be added to your clinic's database and can
                  be used in future prescriptions.
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => {
                      setShowQuickAddForm(false);
                      setQuickAddData({
                        name: "",
                        strength: "",
                        form: "tablet",
                        category: "Supplement",
                      });
                      setErrors({});
                      setManualEntry("");
                    }}
                    style={buttonStyle("secondary")}
                    disabled={isQuickAdding}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleQuickAddMedicine}
                    style={{
                      ...buttonStyle("primary"),
                      opacity: isQuickAdding ? 0.7 : 1,
                      cursor: isQuickAdding ? "not-allowed" : "pointer",
                    }}
                    disabled={isQuickAdding}
                  >
                    {isQuickAdding ? "Adding..." : "Add to Database"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Selected Medicines */}
          {selectedMedicines.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                Selected Medicines ({selectedMedicines.length})
              </h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {selectedMedicines.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                        gap: "12px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: isMobile ? "100%" : "200px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "500",
                            marginBottom: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {getMedicineDisplayName(item)}

                          {item.medicine &&
                            PrescriptionService.hasSafetyWarnings(
                              item.medicine
                            ) && (
                              <span
                                style={{ color: "#dc2626", fontSize: "12px" }}
                              >
                                ⚠️
                              </span>
                            )}

                          {item.medicine?.drug_details && (
                            <button
                              onClick={() => toggleDrugDetails(index)}
                              style={{
                                background: "none",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "10px",
                                cursor: "pointer",
                                color: "#6b7280",
                              }}
                            >
                              {showDrugDetails[index]
                                ? "Hide Info"
                                : "Drug Info"}
                            </button>
                          )}
                        </div>

                        {/* Show details only for database medicines */}
                        {item.medicine && (
                          <div style={{ fontSize: "12px", color: "#64748b" }}>
                            {PrescriptionService.formatMedicineDetails(
                              item.medicine
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => removeMedicine(index)}
                        style={{
                          ...buttonStyle("danger"),
                          padding: "4px 8px",
                          fontSize: "12px",
                          alignSelf: isMobile ? "flex-start" : "flex-start",
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    {/* Drug Details Section */}
                    {item.medicine && renderDrugDetails(item.medicine, index)}

                    {/* Safety Warnings */}
                    {item.medicine &&
                      PrescriptionService.hasSafetyWarnings(item.medicine) && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "8px",
                            background: "#fef2f2",
                            border: "1px solid #fecaca",
                            borderRadius: "4px",
                            fontSize: "12px",
                            color: "#dc2626",
                          }}
                        >
                          <strong>⚠️ Safety Warnings:</strong>
                          <div style={{ marginTop: "4px" }}>
                            {PrescriptionService.getSafetyWarningsText(
                              item.medicine
                            ).map((warning, idx) => (
                              <div key={idx} style={{ marginBottom: "2px" }}>
                                • {warning}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    <div style={medicineGridStyle}>
                      {/* Dosage */}
                      <div>
                        <label
                          htmlFor={`dosage_${index}`}
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          Dosage *
                        </label>
                        <SearchableInput
                          key={`dosage_${index}_${item.medicine_id}`}
                          value={item.dosage}
                          onChange={(value) =>
                            updateMedicineItem(index, "dosage", value)
                          }
                          error={errors[`dosage_${index}`]}
                          autoDetectLanguage={true}
                          category="dosage"
                          onSearchSuggestions={async (query, lang) => {
                            return await PrescriptionService.searchDosageHints(
                              query,
                              lang
                            );
                          }}
                          onCreateNew={async (text, lang) => {
                            return await PrescriptionService.createHintByCategory(
                              text,
                              "dosage",
                              lang
                            );
                          }}
                        />
                      </div>

                      {/* Frequency */}
                      <div>
                        <label
                          htmlFor={`frequency_${index}`}
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          Frequency *
                        </label>
                        <SearchableInput
                          key={`frequency_${index}_${item.medicine_id}`}
                          value={item.frequency}
                          onChange={(value) =>
                            updateMedicineItem(index, "frequency", value)
                          }
                          error={errors[`frequency_${index}`]}
                          autoDetectLanguage={true}
                          category="frequency"
                          onSearchSuggestions={async (query, lang) => {
                            return await PrescriptionService.searchFrequencyHints(
                              query,
                              lang
                            );
                          }}
                          onCreateNew={async (text, lang) => {
                            return await PrescriptionService.createFrequencyHint(
                              text,
                              lang
                            );
                          }}
                        />
                      </div>

                      {/* Duration */}
                      <div>
                        <label
                          htmlFor={`duration_${index}`}
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          Duration
                        </label>
                        <select
                          value={item.duration}
                          onChange={(e) =>
                            updateMedicineItem(
                              index,
                              "duration",
                              e.target.value
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "6px",
                            fontSize: "12px",
                            outline: "none",
                          }}
                        >
                          {durationOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div style={{ marginTop: "12px" }}>
                      <label
                        htmlFor={`doctor_notes_${index}`}
                        style={{
                          display: "block",
                          fontSize: "12px",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        Doctor Notes (Private)
                      </label>
                      <input
                        type="text"
                        id={`doctor_notes_${index}`}
                        name={`doctor_notes_${index}`}
                        value={item.doctor_notes || ""}
                        onChange={(e) =>
                          updateMedicineItem(
                            index,
                            "doctor_notes",
                            e.target.value
                          )
                        }
                        placeholder="Private notes for your reference"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "12px",
                          outline: "none",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Doctor Notes */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              General Notes
            </label>
            <textarea
              value={doctorNotes}
              onChange={(e) => setDoctorNotes(e.target.value)}
              placeholder="Additional notes, advice for patient..."
              rows={3}
              style={{ ...inputStyle, resize: "none" }}
            />
          </div>

          {errors.submit && (
            <div
              style={{
                color: "#ef4444",
                fontSize: "14px",
                marginBottom: "16px",
                padding: "12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Footer */}
          <div style={footerStyle}>
            <div
              style={{
                fontSize: isMobile ? "13px" : "13px",
                color: "#6b7280",
                textAlign: "center",
              }}
            >
              {selectedMedicines.length > 0 && (
                <>{selectedMedicines.length} medicine(s) selected</>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <button
                onClick={onClose}
                style={{
                  ...buttonStyle("secondary"),
                  flex: isMobile ? "1" : "none",
                }}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  ...buttonStyle("primary"),
                  opacity: isSaving ? 0.7 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                  flex: isMobile ? "1" : "none",
                }}
                disabled={isSaving}
              >
                {isSaving
                  ? "💾 Saving..."
                  : isEditMode
                  ? "✅ Update Prescription"
                  : "💾 Save Prescription"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Multiple Similar Medicines Modal */}
      {multipleSimilarMedicines && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
              }}
            >
              Found {multipleSimilarMedicines.found.length} Medicine(s) in
              Database
            </h3>

            {/* Medicines found in database */}
            {multipleSimilarMedicines.found.map((result, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#166534",
                  }}
                >
                  Searching for: "{result.name}"
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    marginBottom: "8px",
                    color: "#6b7280",
                  }}
                >
                  Found {result.similar.length} match(es):
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {result.similar.map((medicine) => (
                    <div
                      key={medicine.id}
                      onClick={() => {
                        addMedicine(medicine);
                        setMultipleSimilarMedicines((prev) => {
                          if (!prev) return null;
                          const newFound = prev.found.filter(
                            (_, i) => i !== idx
                          );
                          if (
                            newFound.length === 0 &&
                            prev.notFound.length === 0
                          ) {
                            return null; // ❌ DELETE THIS CHECK
                          }
                          return { ...prev, found: newFound };
                        });
                      }}
                      style={{
                        padding: "10px",
                        background: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f8fafc")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "white")
                      }
                    >
                      <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                        {PrescriptionService.getMedicineDisplayName(medicine)}
                      </div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>
                        {PrescriptionService.formatMedicineDetails(medicine)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Medicines not found */}
            {multipleSimilarMedicines.notFound.length > 0 && (
              <div
                style={{
                  padding: "12px",
                  background: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: "8px",
                  marginTop: "16px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#92400e",
                  }}
                >
                  Not found in database:
                </div>
                <ul
                  style={{ margin: "0", paddingLeft: "20px", color: "#78350f" }}
                >
                  {multipleSimilarMedicines.notFound.map((result, idx) => (
                    <li key={idx}>{result.name}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Buttons - REMOVE "Use as Custom" button */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <button
                onClick={() => {
                  setMultipleSimilarMedicines(null);
                  setManualEntry("");
                }}
                style={buttonStyle("secondary")}
              >
                Cancel
              </button>

              {/* Only show add to database button */}
              {multipleSimilarMedicines.notFound.length > 0 && (
                <button
                  onClick={() => {
                    setBulkAddMedicines(
                      multipleSimilarMedicines.notFound.map((r) => ({
                        name: r.name,
                        strength: "",
                        form: "tablet",
                        category: "Supplement",
                      }))
                    );
                    setShowBulkAddForm(true);
                    setMultipleSimilarMedicines(null);
                    setManualEntry("");
                  }}
                  style={buttonStyle("primary")}
                >
                  Add to Database ({multipleSimilarMedicines.notFound.length})
                </button>
              )}

              {/* Add new anyway button for found items */}
              {multipleSimilarMedicines.found.length > 0 && (
                <button
                  onClick={() => {
                    setQuickAddData({
                      name: multipleSimilarMedicines.originalSearch,
                      strength: "",
                      form: "tablet",
                      category: "Supplement",
                    });
                    setShowQuickAddForm(true);
                    setMultipleSimilarMedicines(null);
                    setManualEntry("");
                  }}
                  style={{ ...buttonStyle("primary"), background: "#10b981" }}
                >
                  Add New "{multipleSimilarMedicines.originalSearch}"
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Bulk Add to Database Form */}
      {showBulkAddForm && bulkAddMedicines.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
              }}
            >
              Add {bulkAddMedicines.length} Medicine(s) to Database
            </h3>

            {bulkAddMedicines.map((medicine, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: "20px",
                  padding: "16px",
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              >
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "12px",
                  }}
                >
                  {idx + 1}. {medicine.name}
                </h4>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "500",
                        marginBottom: "4px",
                      }}
                    >
                      Strength
                    </label>
                    <input
                      type="text"
                      value={medicine.strength}
                      onChange={(e) => {
                        const updated = [...bulkAddMedicines];
                        updated[idx].strength = e.target.value;
                        setBulkAddMedicines(updated);
                      }}
                      placeholder="e.g., 1000 IU"
                      style={{
                        ...inputStyle,
                        padding: "8px 12px",
                        fontSize: "13px",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "500",
                        marginBottom: "4px",
                      }}
                    >
                      Form
                    </label>
                    <select
                      value={medicine.form}
                      onChange={(e) => {
                        const updated = [...bulkAddMedicines];
                        updated[idx].form = e.target.value;
                        setBulkAddMedicines(updated);
                      }}
                      style={{
                        ...inputStyle,
                        padding: "8px 12px",
                        fontSize: "13px",
                      }}
                    >
                      {medicineFormOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "500",
                        marginBottom: "4px",
                      }}
                    >
                      Category
                    </label>
                    <select
                      value={medicine.category}
                      onChange={(e) => {
                        const updated = [...bulkAddMedicines];
                        updated[idx].category = e.target.value;
                        setBulkAddMedicines(updated);
                      }}
                      style={{
                        ...inputStyle,
                        padding: "8px 12px",
                        fontSize: "13px",
                      }}
                    >
                      {categoryOptions.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}

            {errors.quickAdd && (
              <div
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  marginBottom: "16px",
                  padding: "8px",
                  background: "#fef2f2",
                  borderRadius: "6px",
                }}
              >
                {errors.quickAdd}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <button
                onClick={() => {
                  setShowBulkAddForm(false);
                  setBulkAddMedicines([]);
                  setManualEntry("");
                }}
                style={buttonStyle("secondary")}
                disabled={isQuickAdding}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setIsQuickAdding(true);
                  setErrors({}); // Clear previous errors

                  try {
                    const addedMedicines: string[] = [];
                    const failedMedicines: string[] = [];

                    for (const medicineData of bulkAddMedicines) {
                      try {
                        const newMedicine =
                          await PrescriptionService.quickAddMedicine({
                            name: medicineData.name,
                            strength: medicineData.strength || undefined,
                            form: medicineData.form as any,
                            category: medicineData.category,
                          });
                        addMedicine(newMedicine);
                        addedMedicines.push(newMedicine.name);
                      } catch (error: any) {
                        failedMedicines.push(medicineData.name);
                        console.error(
                          `Failed to add ${medicineData.name}:`,
                          error
                        );
                      }
                    }

                    setShowBulkAddForm(false);
                    setBulkAddMedicines([]);
                    setManualEntry("");

                    // Show appropriate success/error message
                    if (
                      addedMedicines.length > 0 &&
                      failedMedicines.length === 0
                    ) {
                      setSuccessMessage(
                        `${addedMedicines.length} medicine(s) added successfully!`
                      );
                    } else if (
                      addedMedicines.length > 0 &&
                      failedMedicines.length > 0
                    ) {
                      setSuccessMessage(
                        `${addedMedicines.length} added, ${
                          failedMedicines.length
                        } failed: ${failedMedicines.join(", ")}`
                      );
                    } else {
                      setErrors({
                        quickAdd: `Failed to add all medicines: ${failedMedicines.join(
                          ", "
                        )}`,
                      });
                    }

                    setTimeout(() => setSuccessMessage(null), 5000);
                  } catch (error: any) {
                    setErrors({ quickAdd: error.message });
                  } finally {
                    setIsQuickAdding(false);
                  }
                }}
                style={{
                  ...buttonStyle("primary"),
                  opacity: isQuickAdding ? 0.7 : 1,
                  cursor: isQuickAdding ? "not-allowed" : "pointer",
                }}
                disabled={isQuickAdding}
              >
                {isQuickAdding
                  ? "Adding..."
                  : `Add All ${bulkAddMedicines.length} to Database`}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>
        {`
          input:focus, select:focus, textarea:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          
          @media (max-width: 768px) {
            .mobile-stack {
              flex-direction: column !important;
            }
            
            .mobile-full-width {
              width: 100% !important;
            }
            
            .mobile-grid-single {
              grid-template-columns: 1fr !important;
            }
            
            .mobile-text-left {
              text-align: left !important;
            }
          }
          
          @media (max-width: 480px) {
            .mobile-sm-stack {
              flex-direction: column !important;
            }
            
            .mobile-sm-center {
              justify-content: center !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PrescriptionModal;
